import { ContentfulImageAPI } from './contentfulUtils'
import Sites from '@toasttab/shared/src/data/Sites.json'
import { SiteSchema } from '../interfaces/Site'

interface LocalizedEntry {
  language: string
  slug?: string
  slugOverride?: string
}

export const getSupportedRegionsOrLocales = (): string[] => {
  return Sites.sites
    .map((site: any) => site.url)
    .filter((url: any) => url && url.length > 0)
    .map((url: any) => (url.startsWith('/') ? url.slice(1) : url))
}

export const getSiteIdByLanguageCode = (language: string) => {
  let siteId = 1
  switch (language) {
    case 'en-GB':
      siteId = 2
      break
    case 'en-IE':
      siteId = 3
      break
    case 'en-CA':
      siteId = 4
      break
    case 'es-US':
      siteId = 11
      break
    case 'zh-US':
      siteId = 12
      break
    default:
      siteId = 1
  }
  return siteId
}

export const getLanguageByCountryCode = (country: string) => {
  let languageCode = 'en-US'
  switch (country.toUpperCase()) {
    case 'UK':
      languageCode = 'en-GB'
      break
    case 'IE':
      languageCode = 'en-IE'
      break
    case 'CA':
      languageCode = 'en-CA'
      break
    case 'ES-US':
      languageCode = 'es-US'
      break
    case 'ZH-US':
      languageCode = 'zh-US'
      break
    default:
      languageCode = 'en-US'
  }
  return languageCode
}

export const getCountryNameByCountryCode = (country: string) => {
  let countryName = 'United States'
  switch (country.toUpperCase()) {
    case 'UK':
      countryName = 'United Kingdom'
      break
    case 'IE':
      countryName = 'Ireland'
      break
    case 'CA':
      countryName = 'Canada'
      break
    default:
      countryName = 'United States'
  }
  return countryName
}

export const getLocalizedEntries = (
  slug: string,
  regions: any[],
  currentRegion: string
) => {
  regions = regions.filter((region: any) => region.country !== currentRegion)
  return regions.map((region: any) => {
    const language = getLanguageByCountryCode(region.country)
    return {
      slug: slug,
      language: language,
      siteId: getSiteIdByLanguageCode(language)
    }
  })
}

export const getLocaleBySiteId = (siteId: number) => {
  let locale = 'en'
  switch (siteId) {
    case 1:
      locale = 'en'
      break
    case 2:
      locale = 'en'
      break
    case 3:
      locale = 'en'
      break
    case 4:
      locale = 'en'
      break
    case 11:
      locale = 'es-US'
      break
    default:
      locale = 'en'
  }
  return locale
}

export const getAllEntries = (slug: string, regions: any[]) => {
  if (!Array.isArray(regions)) return []
  // Get all contentfulRegionCodes for the input (e.g., ['US', 'UK'])
  const regionCodes = regions.map((region) => region.country?.toUpperCase())

  // From Sites.json, get all languages tied to those regions
  const siteEntries = Sites.sites.filter((site: SiteSchema) =>
    regionCodes.includes(site.contentfulRegionCode)
  )

  // Map each site entry to a localized entry
  return siteEntries.map((site: SiteSchema) => {
    const regionOverride = regions.find(
      (r: any) => r.country?.toUpperCase() === site.contentfulRegionCode
    )
    return {
      slug: slug,
      slugOverride: regionOverride?.slugOverride,
      language: site.language,
      siteId: site.siteId,
      url: site.url
    }
  })
}

export const getSiteUrl = (defaultSiteUrl: string, language: string) => {
  let siteUrl: string | string[] = defaultSiteUrl.split('/')
  let regionString =
    language && language !== 'en-US'
      ? `${language.toLowerCase().split('-')[1]}`
      : ''
  if (regionString === 'gb') {
    regionString = 'uk'
  }
  if (language && language !== 'en-US') {
    siteUrl.splice(3, 0, regionString)
  }
  siteUrl = siteUrl.join('/')
  return siteUrl
}

export type JSONValue =
  | string
  | number
  | boolean
  | { [x: string]: JSONValue }
  | JSONValue[]

export const isAbsoluteUrl = (url: string): boolean => {
  // Regular expression to check if the URL is absolute (starts with http://, https://, or //)
  return /^(?:[a-z]+:)?\/\//i.test(url)
}

export const prependHostName = (
  data: JSONValue,
  hostName: string
): JSONValue => {
  if (Array.isArray(data)) {
    return data.map((item) => prependHostName(item, hostName))
  } else if (typeof data === 'object' && data !== null) {
    const updatedObject: { [key: string]: JSONValue } = {}
    for (const key in data) {
      if (
        (key === 'url' || key === 'href' || key === 'demoUrl') &&
        typeof data[key] === 'string'
      ) {
        const urlValue = data[key] as string
        // Only prepend hostName if the URL is not absolute
        updatedObject[key] = isAbsoluteUrl(urlValue)
          ? urlValue
          : `${hostName}${urlValue}`
      } else {
        updatedObject[key] = prependHostName(data[key], hostName)
      }
    }
    return updatedObject
  }
  return data
}

export const getEntryImages = (hero: any, thumbnailImage?: any) => {
  return [
    {
      heroImage: hero.image && [getImage(hero.image)],
      thumbnailImage: thumbnailImage && [getImage(thumbnailImage)]
    }
  ]
}

export const getImage = (contentfulImage: any) => {
  const image = contentfulImage
  if (image && image.file) {
    const query = ContentfulImageAPI.transformImageAvif()
    const srcset = ContentfulImageAPI.transformImageAvifSrcset({
      url: image.file.url,
      widths: [320, 640, 768, 1024, 1280, 1536, 1920]
    })

    let imageUrl = image.file.url + query
    if (!imageUrl.includes('http')) {
      imageUrl = `https:${imageUrl}`
    }
    return {
      typeHandle: 'image',
      extension: image.file.contentType,
      url: image.file.url,
      title: image.title,
      imgClasses: '',
      optimizedImages: {
        src: image.file.url,
        srcset: srcset,
        srcsetMaxWidth: '',
        srcsetAvif: srcset,
        placeholderBox: '',
        placeholderImage: '',
        maxSrcsetWidth: '',
        srcAvif: imageUrl
      }
    }
  }
}

// Rename oldProperty to newProperty
export const renameProperty = (obj: any, oldProp: string, newProp: string) => {
  if (obj.hasOwnProperty(oldProp)) {
    obj[newProp] = obj[oldProp] // Copy the value to the new property
    delete obj[oldProp] // Delete the old property
  }
}

export const formatUrl = (site: SiteSchema, pkgPath: string, slug: string) => {
  const trimmedPkgPath = pkgPath.replace(/\/+$/, '') // remove trailing slashes
  const normalizedSlug = slug.replace(/^\/+/, '') // remove leading slashes

  // Avoid duplicate slug if it's already at the end of the path
  const shouldSkipSlug =
    trimmedPkgPath.endsWith(`/${normalizedSlug}`) ||
    trimmedPkgPath === normalizedSlug

  const url = shouldSkipSlug
    ? `${site.url}${trimmedPkgPath}`
    : `${site.url}${trimmedPkgPath}/${normalizedSlug}`

  return url.replace(/\/+$/, '') // remove any trailing slash
}

export const normalizeRegion = (regionCode?: string) =>
  regionCode?.toUpperCase() === 'UK' ? 'GB' : regionCode?.toUpperCase() || ''

export const normalizeLang = (lang = '') =>
  lang && lang.toLowerCase().split('-')[0]

export const getRegionLabel = (
  languageName: string,
  regionCode?: string,
  activeRegionCode?: string
) => {
  const isBothUS =
    normalizeRegion(regionCode) === 'US' &&
    normalizeRegion(activeRegionCode) === 'US'

  if (isBothUS) {
    // Strip region and keep language only "English"
    const match = languageName.match(/\((.*?)\)/)
    return match?.[1] || languageName
  }

  return languageName
}

export const getRegionCode = (region: string | undefined) => {
  if (!region) return 'US'
  if (region.toUpperCase() === 'UK') return 'GB'
  return Sites.sites
    .filter((site) => site.defaultRegion)
    .some((site) => site.craftSite === region.toUpperCase())
    ? region.toUpperCase()
    : 'US'
}

export const groupSitesByContinent = (
  sites: SiteSchema[],
  suggestedLanguage: string,
  localizedEntries: LocalizedEntry[],
  activeLanguage: string,
  path: string
): Record<string, (SiteSchema & { url: string; slug: string })[]> =>
  sites.reduce((acc, site) => {
    if (site.language === suggestedLanguage) return acc

    const entry = localizedEntries.find((e) => e.language === site.language)
    const fallback = localizedEntries.find((e) => e.language === activeLanguage)

    const slug =
      entry?.slugOverride ||
      entry?.slug ||
      fallback?.slugOverride ||
      fallback?.slug ||
      ''

    const url = formatUrl(site, path, slug)
    const siteWithUrl = { ...site, url, slug }

    acc[site.continent] = acc[site.continent] || []
    acc[site.continent].push(siteWithUrl)

    return acc
  }, {} as Record<string, (SiteSchema & { url: string; slug: string })[]>)

export function generateUUID(): string {
  // The 'x' and 'y' placeholders are replaced with random hexadecimal digits.
  // The 'y' part also includes logic to set the correct UUID version (4) and variant bits.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    function (c: string): string {
      // Generate a random number between 0 and 15 (inclusive)
      const r: number = (Math.random() * 16) | 0

      // If 'x', use r; if 'y', set variant bits according to UUID spec
      const v: number = c === 'x' ? r : (r & 0x3) | 0x8

      // Convert the number to its hexadecimal representation
      return v.toString(16)
    }
  )
}
